import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react';
import { trpc, RouterOutputs } from '@org/clients/school';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
  storyColors,
  unwrapTrpcData,
} from '@org/ui';

type WomanRow = RouterOutputs['women']['list'][number];

/** The women list, shared by the picker and by whoever shows the chosen woman. */
export function useWomenOptions(): WomanRow[] {
  const { data } = trpc.women.list.useQuery();
  return useMemo(() => unwrapTrpcData<WomanRow[]>(data as WomanRow[]) ?? [], [data]);
}

/** Small round portrait on the cream disc (a dash when she has no portrait yet). */
export function WomanThumb({ portraitUrl, className }: { portraitUrl?: string | null; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border text-[10px] text-muted-foreground',
        className,
      )}
      style={{ backgroundColor: storyColors.portraitDisc }}
    >
      {portraitUrl ? <img src={portraitUrl} alt="" className="h-full w-full object-cover object-top" /> : '—'}
    </span>
  );
}

type WomanSelectProps = {
  value: string | null | undefined;
  onChange: (womanId: string | null) => void;
  disabled?: boolean;
};

/**
 * Searchable picker of the woman a unit introduces, with a "none" choice and
 * a link to the Women page.
 */
export function WomanSelect({ value, onChange, disabled }: WomanSelectProps) {
  const { t } = useTranslation('admin');
  const women = useWomenOptions();
  const [open, setOpen] = useState(false);
  const selected = value ? women.find((woman) => woman.womanId === value) : undefined;

  const choose = (womanId: string | null) => {
    onChange(womanId);
    setOpen(false);
  };

  return (
    <div className="flex flex-col gap-1.5">
      {/* modal: keeps the list scrollable when the picker sits inside a dialog */}
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between font-normal"
          >
            <span className="flex min-w-0 items-center gap-2">
              {selected ? <WomanThumb portraitUrl={selected.portraitUrl} className="h-6 w-6" /> : null}
              <span className={cn('truncate', !selected && 'text-muted-foreground')}>
                {selected ? selected.name : value ? t('women.select.unknown') : t('women.select.none')}
              </span>
            </span>
            <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 max-w-[90vw] p-0">
          <Command>
            <CommandInput placeholder={t('women.select.search')} />
            <CommandList>
              <CommandEmpty>{t('women.select.empty')}</CommandEmpty>
              <CommandGroup>
                <CommandItem value={t('women.select.none')} onSelect={() => choose(null)}>
                  <CheckIcon className={cn('mr-2 h-4 w-4', value ? 'opacity-0' : 'opacity-100')} />
                  {t('women.select.none')}
                </CommandItem>
                {women.map((woman) => (
                  <CommandItem
                    key={woman.womanId}
                    // cmdk filters on this text; the id keeps two women with the same name apart
                    value={`${woman.name} ${woman.womanId}`}
                    onSelect={() => choose(woman.womanId)}
                  >
                    <CheckIcon
                      className={cn('mr-2 h-4 w-4', woman.womanId === value ? 'opacity-100' : 'opacity-0')}
                    />
                    <WomanThumb portraitUrl={woman.portraitUrl} className="mr-2" />
                    <span className="truncate">{woman.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Link to="/women" target="_blank" className="self-start text-xs text-blue-700 underline">
        {t('women.select.manage')}
      </Link>
    </div>
  );
}
